/**
 * @fileoverview «UTM mapping» block on the Meta Ads integration page
 * (Feature B, meta-utm-mapping-coverage). Confirm-not-form (FP-8): one click
 * detects the project's convention from real sessions, the user confirms the
 * pre-filled suggestion; manual override via per-entity selects.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { getMetaAdsConnectService } from '../services/MetaAdsConnectService';
import type {
  MetaAdsConnectionStatus,
  MetaAdsUtmEntity,
  MetaAdsUtmMapping,
  MetaAdsUtmMappingDetectResponse,
  MetaAdsUtmMappingField,
} from '../types';
import { META_ADS_UTM_MAPPING_TEST_IDS } from '../testIds';

const ENTITY_LABELS: Record<MetaAdsUtmEntity, string> = {
  campaign: 'Campaign',
  adset: 'Ad set',
  ad: 'Ad',
};
const ENTITIES: MetaAdsUtmEntity[] = ['campaign', 'adset', 'ad'];
const ENTITY_ARTICLES: Record<MetaAdsUtmEntity, string> = { campaign: 'a', adset: 'an', ad: 'an' };
const FIELD_OPTIONS: MetaAdsUtmMappingField[] = ['utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
/** Degraded when the fresh share drops below this… */
const DEGRADED_SHARE_THRESHOLD = 0.9;
/** …or falls ≥10pp from the confirm-time coverage (SI-8). */
const DEGRADED_DROP_PP = 0.1;
const FB_PARAMS_SNIPPET = 'fb_campaign_id={{campaign.id}}&fb_adset_id={{adset.id}}&fb_ad_id={{ad.id}}';

/** 0.996 must read «99.6%», never a fake «100%» — round to 1 decimal, drop «.0». */
function formatShare(share: number): string {
  const pct = Math.round(share * 1000) / 10;
  return `${Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

export interface MetaAdsUtmMappingSectionProps {
  projectId: string;
  status: MetaAdsConnectionStatus | null;
  adAccountId?: string;
}

export function MetaAdsUtmMappingSection({
  projectId,
  status,
  adAccountId,
}: MetaAdsUtmMappingSectionProps) {
  const service = useMemo(() => getMetaAdsConnectService(), []);
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detect, setDetect] = useState<MetaAdsUtmMappingDetectResponse | null>(null);
  const [draft, setDraft] = useState<Partial<Record<MetaAdsUtmEntity, MetaAdsUtmMappingField | ''>>>({});
  const [savedMapping, setSavedMapping] = useState<MetaAdsUtmMapping | null | undefined>(undefined);

  const mapping = savedMapping !== undefined ? savedMapping : status?.utmMapping ?? null;

  const runDetect = useCallback(async () => {
    setDetecting(true);
    setError(null);
    try {
      const result = await service.detectUtmMapping(projectId, adAccountId || undefined);
      setDetect(result);
      setDraft({
        campaign: result.suggestion.campaign?.field ?? '',
        adset: result.suggestion.adset?.field ?? '',
        ad: result.suggestion.ad?.field ?? '',
      });
    } catch (detectError) {
      setError(detectError instanceof Error ? detectError.message : 'Failed to detect UTM mapping');
    } finally {
      setDetecting(false);
    }
  }, [service, projectId, adAccountId]);

  const applyMapping = useCallback(async () => {
    if (!detect) return;
    setSaving(true);
    setError(null);
    try {
      const payload: MetaAdsUtmMapping = {};
      for (const entity of ENTITIES) {
        const field = draft[entity];
        if (field) payload[entity] = field;
      }
      payload.detectedCoverage = {
        campaign: detect.fields[draft.campaign as MetaAdsUtmMappingField]?.idShare.campaign,
        adset: detect.fields[draft.adset as MetaAdsUtmMappingField]?.idShare.adset,
        ad: detect.fields[draft.ad as MetaAdsUtmMappingField]?.idShare.ad,
      };
      const saved = await service.setUtmMapping(projectId, payload);
      // The POST response is the AUTHORITATIVE saved state (M2): parent status
      // refreshes were remounting the section and wiping it mid-transition.
      setSavedMapping(saved.utmMapping);
      setDetect(null);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save UTM mapping');
    } finally {
      setSaving(false);
    }
  }, [service, projectId, detect, draft]);

  const clearMapping = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await service.setUtmMapping(projectId, null);
      setSavedMapping(null);
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : 'Failed to clear UTM mapping');
    } finally {
      setSaving(false);
    }
  }, [service, projectId]);

  // Degradation: a fresh detect shows the confirmed field's id-share dropped
  // below the threshold (new campaigns are probably missing URL parameters).
  const degradedEntities = useMemo(() => {
    if (!mapping || !detect) return [] as MetaAdsUtmEntity[];
    return ENTITIES.filter(entity => {
      const field = mapping[entity];
      if (!field) return false;
      const share = detect.fields[field]?.idShare[entity] ?? 0;
      const confirmShare = mapping.detectedCoverage?.[entity];
      return share < DEGRADED_SHARE_THRESHOLD
        || (typeof confirmShare === 'number' && confirmShare - share >= DEGRADED_DROP_PP);
    });
  }, [mapping, detect]);

  const suggestionVisible = Boolean(detect) && !mapping;
  const anyDraftSelected = ENTITIES.some(entity => draft[entity]);

  return (
    <Box data-testid={META_ADS_UTM_MAPPING_TEST_IDS.section} sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        UTM mapping
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tell Segmently which UTM parameter of your ad links carries which Meta id. This unlocks
        ad-level economics in Reports without changing your links. Without a confirmed mapping,
        automatic matching (fb_* params and utm_id) keeps working as before.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Degradation warning — reuses the sync-alert surface pattern. */}
      {mapping && degradedEntities.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid={META_ADS_UTM_MAPPING_TEST_IDS.degradedAlert}>
          <AlertTitle>UTM mapping coverage dropped</AlertTitle>
          {degradedEntities.map(entity => {
            const field = mapping[entity]!;
            const share = detect?.fields[field]?.idShare[entity] ?? 0;
            return (
              <Typography key={entity} variant="body2">
                {`Only ${formatShare(share)} of tagged sessions carry ${ENTITY_ARTICLES[entity]} ${ENTITY_LABELS[entity].toLowerCase()} id in ${field}`}
                {typeof mapping.detectedCoverage?.[entity] === 'number'
                  ? ` (was ${formatShare(mapping.detectedCoverage[entity] ?? 0)} at confirm time).`
                  : '.'}
              </Typography>
            );
          })}
          <Typography variant="body2" sx={{ mt: 1 }}>
            New campaigns are probably missing URL parameters. Recommended belt-and-suspenders — add
            Meta dynamic parameters to every ad:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Box
              component="code"
              data-testid={META_ADS_UTM_MAPPING_TEST_IDS.snippet}
              sx={{ flex: 1, p: 1, fontSize: '0.76rem', bgcolor: 'action.hover', borderRadius: 1, overflowWrap: 'anywhere' }}
            >
              {FB_PARAMS_SNIPPET}
            </Box>
            <Button
              size="small"
              data-testid={META_ADS_UTM_MAPPING_TEST_IDS.snippetCopy}
              onClick={() => { void navigator.clipboard?.writeText(FB_PARAMS_SNIPPET); }}
            >
              Copy
            </Button>
          </Stack>
        </Alert>
      )}

      {mapping ? (
        <Box data-testid={META_ADS_UTM_MAPPING_TEST_IDS.savedCard}>
          {/* M1 (FP-7): when a fresh detect shows degradation, the warning above
              is the ONE truth — no contradicting success alert, and the chips
              carry the FRESH shares with ⚠, never the stale confirm-time ones. */}
          {degradedEntities.length === 0 && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              <AlertTitle>Mapping active</AlertTitle>
              {mapping.confirmedAt ? `Confirmed ${mapping.confirmedAt.slice(0, 10)}` : 'Confirmed'}
              {mapping.confirmedBy ? ` by ${mapping.confirmedBy}` : ''} · Reports match sessions by this
              mapping (label: «Project UTM mapping») in addition to fb_* and utm_id. Campaign matching
              is automatic either way — the ad set / ad rows are what unlock ad-level economics.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}>
            {ENTITIES.filter(entity => mapping[entity]).map(entity => {
              const field = mapping[entity]!;
              const freshShare = detect ? detect.fields[field]?.idShare[entity] : undefined;
              const confirmShare = mapping.detectedCoverage?.[entity];
              const degraded = degradedEntities.includes(entity);
              const shareLabel = typeof freshShare === 'number'
                ? ` · ${degraded ? '⚠ ' : ''}now ${formatShare(freshShare)}${
                    typeof confirmShare === 'number' ? ` (was ${formatShare(confirmShare)})` : ''
                  }`
                : typeof confirmShare === 'number'
                  ? ` · ${formatShare(confirmShare)} at confirm`
                  : '';
              return (
                <Chip
                  key={entity}
                  size="small"
                  variant="outlined"
                  color={degraded ? 'warning' : 'default'}
                  data-testid={META_ADS_UTM_MAPPING_TEST_IDS.savedChip(entity)}
                  label={`${ENTITY_LABELS[entity]} → ${field}${shareLabel}`}
                />
              );
            })}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={runDetect}
              disabled={detecting || saving}
              data-testid={META_ADS_UTM_MAPPING_TEST_IDS.redetectButton}
              startIcon={detecting ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {detecting ? 'Re-detecting…' : 'Re-detect'}
            </Button>
            <Button size="small" onClick={clearMapping} disabled={saving || detecting} data-testid={META_ADS_UTM_MAPPING_TEST_IDS.clearButton}>
              Remove mapping
            </Button>
          </Stack>
        </Box>
      ) : suggestionVisible ? (
        <Box data-testid={META_ADS_UTM_MAPPING_TEST_IDS.suggestionCard}>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <AlertTitle>
              {`Detection finished — ${detect!.analyzedSessions.toLocaleString()} sessions analyzed (last ${detect!.windowDays} days)`}
            </AlertTitle>
            {Object.keys(detect!.suggestion).length > 0
              ? 'Your links already carry Meta ids. Confirm the mapping below to unlock ad-level economics.'
              : Object.keys(detect!.nameEvidence).length > 0
                ? 'Your links carry Meta NAMES, not ids — names are unstable (renames, duplicates). Switch your URL parameters to ids for exact economics:'
                : 'No Meta ids found in the UTM fields of your sessions. Add Meta dynamic parameters to your ads:'}
            {Object.keys(detect!.suggestion).length === 0 && (
              <Box component="code" sx={{ display: 'block', mt: 1, p: 1, fontSize: '0.76rem', bgcolor: 'action.hover', borderRadius: 1, overflowWrap: 'anywhere' }}>
                {FB_PARAMS_SNIPPET}
              </Box>
            )}
          </Alert>
          {ENTITIES.map(entity => {
            const suggested = detect!.suggestion[entity];
            const nameHint = detect!.nameEvidence[entity];
            return (
              <Stack
                key={entity}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
                sx={{ py: 1, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
                data-testid={META_ADS_UTM_MAPPING_TEST_IDS.entityRow(entity)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                  {ENTITY_LABELS[entity]}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {suggested
                    ? `${formatShare(suggested.share)} of tagged sessions: ${suggested.field} is ${ENTITY_ARTICLES[entity]} ${ENTITY_LABELS[entity].toLowerCase()} id`
                    : nameHint
                      ? `${formatShare(nameHint.share)} of ${nameHint.field} values are ${ENTITY_LABELS[entity].toLowerCase()} NAMES — ids required for matching`
                      : 'No qualifying utm field found'}
                </Typography>
                <Select
                  size="small"
                  value={draft[entity] ?? ''}
                  onChange={event => setDraft(prev => ({ ...prev, [entity]: event.target.value as MetaAdsUtmMappingField | '' }))}
                  displayEmpty
                  sx={{ minWidth: 170 }}
                  data-testid={META_ADS_UTM_MAPPING_TEST_IDS.entitySelect(entity)}
                >
                  <MenuItem value="">Not used</MenuItem>
                  {FIELD_OPTIONS.map(field => (
                    <MenuItem key={field} value={field}>{field}</MenuItem>
                  ))}
                </Select>
              </Stack>
            );
          })}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={applyMapping}
              disabled={saving || !anyDraftSelected}
              data-testid={META_ADS_UTM_MAPPING_TEST_IDS.applyButton}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {saving ? 'Applying…' : 'Apply mapping'}
            </Button>
            <Button size="small" onClick={() => setDetect(null)} disabled={saving} data-testid={META_ADS_UTM_MAPPING_TEST_IDS.cancelButton}>
              Cancel
            </Button>
            <Typography variant="caption" color="text.secondary">
              Only values that are real ids of your connected ad account will match — mixed or foreign
              tags are ignored safely.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            size="small"
            onClick={runDetect}
            disabled={detecting}
            data-testid={META_ADS_UTM_MAPPING_TEST_IDS.detectButton}
            startIcon={detecting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {detecting ? 'Detecting…' : 'Detect from sessions'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Analyzes your last 30 days of tagged sessions against synced Meta campaigns, ad sets and
            ads. Read-only, takes a few seconds.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default MetaAdsUtmMappingSection;
